import { useEffect, useMemo, useState } from 'react';
import type { Hausanschluss, SmartMeterGateway } from '../../../../types/network';
import { PlusIcon, TrashIcon, ChevronIcon } from '../../../icons/ButtonIcons';
import { CopyableId } from '../../../shared/CopyableId';
import { SmgwHistoryModal } from '../smgw/SmgwHistoryModal';
import { PhaseRotationBadge } from '../smgw/PhaseRotationBadge';
import { useSettingsStore } from '../../../../store/settingsStore';
import { useReadOnlyUi } from '../../../../store/featureStore';
import { useHistoricalPlaybackStore, type SnapshotMeterValue } from '../../../../store/historicalPlaybackStore';
import { useNetworkElementsStore } from '../../../../store/networkElementsStore';
import { authService } from '../../../../services/authService';
import { getSmgwHistory } from '../../../../services/maasService';
import { buildMeterRows } from '../../../../utils/meterRows';
import { shortMeloLabel } from '../../../../utils/smgwUtils';
import { fmt, fmtInt } from '../../../../utils/numberFormat';
import { DirectionBadge } from './MeterBadges';
import { MeterRowCard } from './MeterRowCard';
import { ExactMeterValue } from './TafLiveView';
import { OBIS_EXPORT, OBIS_IMPORT, formatPower } from '../../../../utils/meterFormat';

interface HistoryModalState {
  serialNumber: string;
  tafLogicalName?: string;
  meterLabel?: string;
}

/**
 * Sum of all meters' values at the rewound instant, next to the loadflow value
 * — makes multi-meter houses reconcilable at a glance. With exact snapshot meter
 * inputs (meterValues) both numbers match by construction; without them (old
 * archives) the hourly archive averages of the power registers are summed.
 */
const HistoricalMetersSum = ({ smgws, anchorIso, loadflowKw, exactValues }: {
  smgws: SmartMeterGateway[];
  anchorIso: string;
  loadflowKw: number | null;
  exactValues: SnapshotMeterValue[] | undefined;
}) => {
  const [hourSumKw, setHourSumKw] = useState<number | null | 'loading'>('loading');
  const hasExact = exactValues !== undefined;
  const serials = smgws.map(s => s.serialNumber).filter(Boolean).join('|');
  useEffect(() => {
    if (hasExact) return;
    let cancelled = false;
    setHourSumKw('loading');
    Promise.all(serials.split('|').filter(Boolean).map(serial =>
      getSmgwHistory(serial, '24h', undefined, anchorIso).catch(() => null))
    ).then(responses => {
      if (cancelled) return;
      let total = 0;
      let any = false;
      for (const data of responses) {
        // Only the active-power registers: voltages, currents, frequency are no meter value
        for (const series of data?.series ?? []) {
          if (series.obisCode !== OBIS_IMPORT && series.obisCode !== OBIS_EXPORT) continue;
          const last = series.dataPoints[series.dataPoints.length - 1];
          if (!last) continue;
          any = true;
          total += (series.obisCode === OBIS_EXPORT ? -1 : 1) * last.avgValue;
        }
      }
      setHourSumKw(any ? total / 1000 : null);
    });
    return () => { cancelled = true; };
  }, [serials, anchorIso, hasExact]);

  if (hasExact) {
    const sumKw = (exactValues ?? []).reduce((acc, v) => acc + v.powerW, 0) / 1000;
    return (
      <div
        className="mb-2 px-2 py-1.5 rounded bg-gray-50 dark:bg-primary-700/50 border border-gray-200 dark:border-primary-600 flex items-center justify-between text-xs"
        title="Summe der archivierten Zähler-Werte zum Berechnungszeitpunkt — die Eingangsgrößen des gespeicherten Lastflusses."
      >
        <span className="text-gray-600 dark:text-primary-300">Σ Zähler (15 min)</span>
        <span className="font-mono font-semibold text-gray-900 dark:text-white">
          {fmt(sumKw)} kW
          {loadflowKw != null && <span className="font-normal text-gray-500 dark:text-primary-400"> · Lastfluss: {fmt(loadflowKw)} kW</span>}
        </span>
      </div>
    );
  }
  if (hourSumKw === 'loading' || hourSumKw == null) return null;
  return (
    <div
      className="mb-2 px-2 py-1.5 rounded bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 flex items-center justify-between text-xs"
      title="Summe der Stundenmittel aller Zähler vs. der 15-Minuten-Lastflusswert des gewählten Zeitpunkts. Abweichungen entstehen durch die unterschiedliche Mittelung."
    >
      <span className="text-orange-800 dark:text-orange-200">Σ Zähler (Ø Stunde)</span>
      <span className="font-mono font-semibold text-orange-800 dark:text-orange-200">
        {fmt(hourSumKw)} kW
        {loadflowKw != null && <span className="font-normal opacity-80"> · Lastfluss (15 min): {fmt(loadflowKw)} kW</span>}
      </span>
    </div>
  );
};

/**
 * "Zähler" card of a Hausanschluss: every Zähler (ERP Messlokation and/or SMGW
 * meter) once, its current value with the source next to it, the house total
 * split into measured and estimated, and per meter one view per TAF profile.
 * Gateways are listed (and managed) at the bottom — the SMGW is only the
 * communication unit, the Zähler is what the user looks for.
 */
export const MetersSection = ({ element, onUpdateSmgws }: {
  element: Hausanschluss;
  onUpdateSmgws: (smgws: SmartMeterGateway[]) => void;
}) => {
  const smgws = useMemo(() => element.smgws ?? [], [element.smgws]);
  const melos = useMemo(() => element.melos ?? [], [element.melos]);
  const summary = useMemo(() => buildMeterRows(smgws, melos, element.meloCurrentPool), [smgws, melos, element.meloCurrentPool]);
  const maasUrl = useSettingsStore(state => state.settings?.maasUrl ?? '');
  const readOnly = useReadOnlyUi();
  const playbackMode = useHistoricalPlaybackStore(state => state.mode);
  const playbackTs = useHistoricalPlaybackStore(state => state.effectiveTs);
  const historicalAnchor = playbackMode === 'historical' && playbackTs ? playbackTs : null;
  // Any frame that is not "now" (Netzanschluss check, rewind still loading): live meter values do not belong to it
  const frameMode = playbackMode !== 'live' && historicalAnchor == null;
  const liveView = playbackMode === 'live';
  const snapshotHasMeterValues = useHistoricalPlaybackStore(state => state.meterValuesByElement !== null);
  const exactValuesRaw = useHistoricalPlaybackStore(state => state.meterValuesByElement?.[element.id]);
  const exactValues = useMemo(() => (snapshotHasMeterValues ? exactValuesRaw ?? [] : undefined), [snapshotHasMeterValues, exactValuesRaw]);
  const loadflowKw = useNetworkElementsStore(state => state.powerMeasurements[element.id]?.totalActivePower ?? null);
  const pvInstalledKw = (element.technischeRessourcen ?? [])
    .filter(r => r.type === 'pv_anlage')
    .reduce((acc, r) => acc + (r.maxPower ?? 0), 0);
  const [isExpanded, setIsExpanded] = useState(true);
  const [historyModal, setHistoryModal] = useState<HistoryModalState | null>(null);

  const rows = summary.rows;
  const feedInCount = rows.filter(r => r.direction === 'einspeisung').length;
  const allMelos = useMemo(
    () => [...new Set(rows.flatMap(r => [r.melo?.id, ...r.meters.flatMap(m => m.group.meLoIds)]).filter((m): m is string => !!m))],
    [rows]);
  const showLiveTotal = liveView && summary.totalW != null;

  // Archive inputs without a row — e.g. an estimated PV feed-in at a connection without a generation series.
  const orphans = useMemo(() => {
    if (!historicalAnchor || exactValues === undefined) return [];
    const knownMelos = new Set(allMelos.map(m => m.toUpperCase()));
    const knownSerials = new Set(smgws.map(s => s.serialNumber).filter(Boolean));
    return exactValues.filter(v =>
      !(v.meloId != null && knownMelos.has(v.meloId.toUpperCase()))
      && !(v.meloId == null && knownSerials.has(v.serialNumber)));
  }, [historicalAnchor, exactValues, allMelos, smgws]);

  const openMaas = (serial: string) => {
    const token = authService.getToken();
    const redirect = `/smart-meter-gateways/${encodeURIComponent(serial)}`;
    const url = token
      ? `${maasUrl}/sso-login?token=${encodeURIComponent(token)}&redirect=${encodeURIComponent(redirect)}`
      : `${maasUrl}${redirect}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="relative bg-white dark:bg-primary-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-primary-600">
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-teal-400 dark:bg-teal-500" />
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left pl-4 pr-3 py-2.5 hover:bg-gray-50 dark:hover:bg-primary-700/30 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="shrink-0 w-6 h-6 rounded-full bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
          </div>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Zähler</span>
          {summary.meterCount > 0 && <span className="text-[10px] text-gray-500 dark:text-gray-400 font-medium">{summary.meterCount}</span>}
          {feedInCount > 0 && (
            <span
              className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300"
              title={`${feedInCount} Einspeise-Messpunkt${feedInCount === 1 ? '' : 'e'} an diesem Anschluss — hier wird Erzeugung (z. B. PV) gemessen.`}
            >
              ↑ {feedInCount} Einspeisung
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {showLiveTotal && <span className="text-xs font-mono font-semibold text-gray-900 dark:text-white">Σ {formatPower(summary.totalW!)}</span>}
          <ChevronIcon direction={isExpanded ? 'up' : 'down'} className="w-4 h-4 text-gray-400 dark:text-gray-500" />
        </div>
      </button>

      {isExpanded && (
        <div className="pl-4 pr-3 pb-3 space-y-2">
          {/* PV in the model but no feed-in series in the archive: the generation is missing in the calculation */}
          {historicalAnchor && pvInstalledKw > 0 && feedInCount === 0 && !(exactValues ?? []).some(v => v.obisCode === OBIS_EXPORT) && (
            <div
              className="px-2 py-1.5 rounded bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 text-xs text-orange-800 dark:text-orange-200"
              title="Das Netzmodell führt an diesem Anschluss eine PV-Anlage (Technische Ressourcen), es ist aber kein Einspeise-Messpunkt mit Messreihe zugeordnet."
            >
              {fmt(pvInstalledKw, 1)} kW PV laut Modell installiert, aber kein Einspeise-Messpunkt zugeordnet — Erzeugung fehlt in der Berechnung.
            </div>
          )}
          {historicalAnchor && smgws.some(s => s.serialNumber) && (
            <HistoricalMetersSum smgws={smgws} anchorIso={historicalAnchor} loadflowKw={loadflowKw} exactValues={exactValues} />
          )}

          {showLiveTotal && (
            <div className="rounded bg-gray-50 dark:bg-primary-700/50 px-2 py-1.5 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-300">Aktuell alle Zähler</span>
                <span className="font-mono font-semibold text-gray-900 dark:text-white">{formatPower(summary.totalW!)}</span>
              </div>
              <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px]">
                {summary.measuredCount > 0 && (
                  <span className="text-success-700 dark:text-success-300" title="Summe der Live-Werte (TAF 10) dieser Zähler">
                    {formatPower(summary.measuredW)} gemessen ({summary.measuredCount} {summary.measuredCount === 1 ? 'Zähler' : 'Zähler'}, TAF 10)
                  </span>
                )}
                {summary.estimatedCount > 0 && (
                  <span className="text-orange-700 dark:text-orange-300" title="Zähler ohne Messung: aus dem korrigierten Hauswert bzw. Lastprofil und Jahresverbrauch geschätzt">
                    {formatPower(summary.estimatedW)} geschätzt ({summary.estimatedCount} Zähler)
                  </span>
                )}
              </div>
            </div>
          )}
          {historicalAnchor && melos.length > 0 && (
            <div className="text-[10px] text-gray-400 dark:text-primary-400 italic" title="Die aktuellen Zählerwerte gehören zu jetzt, nicht zum gewählten Zeitpunkt.">
              Historische Ansicht: Werte je Zähler aus dem Archiv, Live-Schätzungen ausgeblendet.
            </div>
          )}
          {frameMode && rows.length > 0 && (
            <div className="text-[10px] text-gray-400 dark:text-primary-400 italic" title="Die Karte zeigt einen berechneten Fall, die aktuellen Zählerwerte gehören nicht dazu.">
              Berechneter Fall auf der Karte: aktuelle Zählerwerte ausgeblendet.
            </div>
          )}

          {rows.length === 0 ? (
            <div className="text-xs text-gray-400 dark:text-primary-400 px-1 py-1">Keine Zähler bekannt.</div>
          ) : (
            <div className="space-y-1.5">
              {rows.map((row, index) => (
                <MeterRowCard
                  key={row.key}
                  row={row}
                  index={index}
                  frameMode={frameMode}
                  siblingMelos={allMelos}
                  historicalAnchor={historicalAnchor}
                  exactValues={exactValues}
                  pvInstalledKw={pvInstalledKw}
                  onShowHistory={(serialNumber, tafLogicalName, meterLabel) => setHistoryModal({ serialNumber, tafLogicalName, meterLabel })}
                />
              ))}
            </div>
          )}

          {liveView && summary.poolEstimate && (
            <div
              className="text-[11px] text-gray-500 dark:text-gray-400 px-1"
              title="Am Anschluss gibt es Messwerte, die die Registry keiner Messlokation zuordnet. Für die übrigen Zähler wird je ein Wert aus dem durchschnittlichen Jahresverbrauch geschätzt."
            >
              + {summary.poolEstimate.count} Zähler ohne Messung, je ~{formatPower(summary.poolEstimate.perMeterW)} geschätzt
            </div>
          )}

          {orphans.length > 0 && historicalAnchor && (
            <div>
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Weitere Werte in der Berechnung</div>
              <div className="space-y-1.5">
                {orphans.map((v, i) => (
                  <div key={`${v.serialNumber}-${v.meloId ?? i}`} className="bg-gray-50 dark:bg-primary-700/50 rounded-md border border-gray-100 dark:border-primary-600 px-2.5 py-2">
                    <span className="flex items-center gap-1.5 min-w-0">
                      <DirectionBadge direction={v.obisCode === OBIS_EXPORT ? 'einspeisung' : 'bezug'} />
                      {v.meloId && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate">
                          MeLo <CopyableId value={v.meloId} title="Messlokation" className="text-xs font-mono">{shortMeloLabel(v.meloId, orphans.map(o => o.meloId ?? ''))}</CopyableId>
                        </span>
                      )}
                    </span>
                    <ExactMeterValue value={v} effectiveTs={historicalAnchor} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {summary.annualKwh > 0 && (
            <div className="flex justify-between items-center px-1 text-[11px]">
              <span className="text-gray-500 dark:text-gray-400">Jahresverbrauch aller Messlokationen</span>
              <span className="font-semibold text-info-600 dark:text-info-400">{fmtInt(summary.annualKwh)} kWh</span>
            </div>
          )}
          {element.meLoId && element.meLoId !== melos[0]?.id && (
            <div className="px-1 text-[10px] text-gray-500 dark:text-gray-400">
              Virtuelle MeLoId: <CopyableId value={element.meLoId} className="font-mono text-[10px]" />
            </div>
          )}

          {/* Gateways: communication units, managed here (one SMGW can serve several Zähler) */}
          {(smgws.length > 0 || !readOnly) && (
            <div className="pt-2 border-t border-gray-100 dark:border-primary-600 space-y-1">
              <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-primary-400">Smart Meter Gateways</div>
              {smgws.map((smgw, index) => (
                <div key={smgw.id} className="flex items-center justify-between gap-1 text-xs">
                  <span className="flex items-center gap-1.5 min-w-0">
                    {smgw.serialNumber
                      ? <CopyableId value={smgw.serialNumber} title="SMGW-Seriennummer" className="text-xs text-gray-700 dark:text-gray-200" />
                      : <span className="text-gray-400">Keine Seriennummer</span>}
                    {smgw.hasRealtimeData && <span className="w-2 h-2 rounded-full bg-success-500 animate-pulse shrink-0" title="Live-Werte (TAF 10) kommen an" />}
                    <PhaseRotationBadge rotation={smgw.phaseRotation} />
                  </span>
                  <span className="flex items-center shrink-0">
                    {maasUrl && smgw.serialNumber && (
                      <button
                        onClick={() => openMaas(smgw.serialNumber)}
                        className="text-teal-600 dark:text-teal-400 hover:bg-teal-500/10 dark:hover:bg-teal-500/20 p-1 rounded transition-colors"
                        title="Im MaaS-Portal des Messstellenbetreibers anzeigen"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    )}
                    {!readOnly && (
                      <button
                        onClick={() => onUpdateSmgws(smgws.filter((_, i) => i !== index))}
                        className="text-error-600 dark:text-error-400 hover:bg-error-500/10 dark:hover:bg-error-400/10 p-1 rounded transition-colors"
                        title="SMGW entfernen"
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </span>
                </div>
              ))}
              {!readOnly && (
                <button
                  onClick={() => onUpdateSmgws([...smgws, { id: crypto.randomUUID(), serialNumber: '' }])}
                  className="w-full text-teal-600 dark:text-teal-400 hover:bg-teal-500/10 dark:hover:bg-teal-500/20 border border-teal-300 dark:border-teal-700 h-7 flex items-center justify-center gap-1 rounded-md text-xs"
                  title="SMGW hinzufügen"
                >
                  <PlusIcon />
                  <span>SMGW hinzufügen</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <SmgwHistoryModal
        isOpen={historyModal !== null}
        serialNumber={historyModal?.serialNumber ?? ''}
        tafLogicalName={historyModal?.tafLogicalName}
        meterLabel={historyModal?.meterLabel}
        onClose={() => setHistoryModal(null)}
      />
    </div>
  );
};
